/**
 * AIProviderPanel
 * ---------------
 *
 * September 2026
 *
 * Lets the user connect an AI provider (Anthropic, OpenAI, OpenRouter, or
 * any OpenAI-Chat-Completions-compatible endpoint) for AI-generated
 * narrative analysis. The key is validated server-side, then stored
 * encrypted in the Flask session (/api/ai/key) and never echoed back -- this
 * panel only ever knows whether a provider is connected, not the key's value.
 * Lives on the AI Settings page; other pages read the connection status via
 * useAIProviderStatus.
 */
import * as React from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";

import { announceProviderStatus, describeProviderStatus, toProviderStatus } from "./useAIProviderStatus.js";
import { ASSISTANT_NAME } from "../Assistant/AssistantBubble.js";

const ALL_PROVIDERS = [
  { label: "Anthropic (Claude)", value: "anthropic" },
  { label: "OpenAI", value: "openai" },
  { label: "OpenRouter", value: "openrouter" },
  { label: "Local (Ollama / Open WebUI / Docker Model Runner)", value: "local" },
  { label: "Custom (OpenAI-compatible)", value: "custom" },
];

// Shown until the server says which providers it allows (GET /api/ai/config).
const HOSTED_PROVIDERS = ["anthropic", "openai", "openrouter"];

// Providers with a caller-supplied endpoint; the key is optional.
const ENDPOINT_PROVIDERS = ["local", "custom"];

// Providers where the model is picked from a fetched list.
const MODEL_PICKER_PROVIDERS = ["openrouter", ...ENDPOINT_PROVIDERS];

// The backend runs in Docker, so these use the host's Docker names, not localhost.
const LOCAL_PRESETS = [
  { label: "Open WebUI (enter your host)", baseUrl: "" },
  { label: "Ollama (host.docker.internal:11434)", baseUrl: "http://host.docker.internal:11434/v1/chat/completions" },
  { label: "Docker Model Runner (model-runner.docker.internal)", baseUrl: "http://model-runner.docker.internal/engines/llama.cpp/v1/chat/completions" },
];

const OPENROUTER_FREE_ROUTER = "openrouter/free";

const REMEMBERED_CONFIG_KEY = "doeAnalyzer.aiProviderConfig";

function loadRememberedConfig() {
  try {
    return JSON.parse(window.localStorage.getItem(REMEMBERED_CONFIG_KEY)) || {};
  } catch {
    return {};
  }
}

function rememberConfig(config) {
  try {
    window.localStorage.setItem(REMEMBERED_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // Best-effort only -- a private window or blocked storage just means no memory next time.
  }
}

// Local hosts return model ids; OpenRouter returns {id, name, free, context_length}.
function toModelOption(model) {
  if (typeof model === "string") return { label: model, value: model, free: false };
  const context = model.context_length ? ` · ${Math.round(model.context_length / 1000)}k ctx` : "";
  return { label: `${model.name}${context}`, value: model.id, free: !!model.free };
}

function AIProviderPanel({ onStatusChange }) {
  const [status, setStatus] = React.useState(toProviderStatus({ has_key: false }));

  const remembered = React.useMemo(loadRememberedConfig, []);
  const [provider, setProvider] = React.useState(remembered.provider || "anthropic");
  const [apiKeyInput, setApiKeyInput] = React.useState("");
  const [baseUrl, setBaseUrl] = React.useState(remembered.baseUrl || "");
  const [model, setModel] = React.useState(
    remembered.model || (remembered.provider === "openrouter" ? OPENROUTER_FREE_ROUTER : ""),
  );
  const [modelOptions, setModelOptions] = React.useState([]);
  const [freeOnly, setFreeOnly] = React.useState(remembered.freeOnly ?? true);
  const [fetchingModels, setFetchingModels] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Provider whose key the server kept at the last disconnect (reused if it's picked again).
  const [savedProvider, setSavedProvider] = React.useState(null);
  const [testResult, setTestResult] = React.useState(null);
  const [testing, setTesting] = React.useState(false);
  const [allowedProviders, setAllowedProviders] = React.useState(HOSTED_PROVIDERS);

  React.useEffect(() => {
    axios
      .get("/api/ai/config")
      .then((res) => setAllowedProviders(res.data.providers || HOSTED_PROVIDERS))
      .catch(() => setAllowedProviders(HOSTED_PROVIDERS));
  }, []);

  React.useEffect(() => {
    if (!allowedProviders.includes(provider)) {
      setProvider(allowedProviders[0]);
      setBaseUrl("");
      setModel("");
      setModelOptions([]);
    }
    // Only when the allowed list changes, not on every provider edit.
  }, [allowedProviders]);

  const visibleProviders = ALL_PROVIDERS.filter((p) => allowedProviders.includes(p.value));
  const allows = (value) => allowedProviders.includes(value);

  const isEndpointProvider = ENDPOINT_PROVIDERS.includes(provider);
  const isOpenRouter = provider === "openrouter";
  const hasSavedKey = savedProvider === provider;

  const reportStatus = (data) => {
    const next = toProviderStatus(data);
    setStatus(next);
    setSavedProvider(data?.saved_provider || null);
    setTestResult(null);
    announceProviderStatus(next);
    if (onStatusChange) onStatusChange(next);
  };

  React.useEffect(() => {
    axios
      .get("/api/ai/key/status")
      .then((res) => reportStatus(res.data))
      .catch(() => reportStatus({ has_key: false }));
  }, []);

  const fetchModels = (forProvider = provider) => {
    if (forProvider !== "openrouter" && !baseUrl.trim()) return;
    setFetchingModels(true);
    setError(null);
    axios
      .post("/api/ai/models", {
        provider: forProvider,
        base_url: forProvider === "openrouter" ? undefined : baseUrl.trim(),
        api_key: apiKeyInput.trim() || undefined,
      })
      .then((res) => setModelOptions((res.data.models || []).map(toModelOption)))
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to list models.");
        setModelOptions([]);
      })
      .finally(() => setFetchingModels(false));
  };

  // OpenRouter's model list is public, so load it right away.
  React.useEffect(() => {
    if (isOpenRouter && !status.hasKey && modelOptions.length === 0) fetchModels("openrouter");
  }, [isOpenRouter, status.hasKey]);

  const shownModelOptions = isOpenRouter && freeOnly ? modelOptions.filter((m) => m.free) : modelOptions;
  const freeCount = modelOptions.filter((m) => m.free).length;

  const connect = () => {
    if (!apiKeyInput.trim() && !isEndpointProvider && !hasSavedKey) return;
    setLoading(true);
    setError(null);
    axios
      .post("/api/ai/key", {
        provider,
        api_key: apiKeyInput.trim() || undefined,
        base_url: isEndpointProvider ? baseUrl.trim() || undefined : undefined,
        model: model.trim() || undefined,
      })
      .then((res) => {
        reportStatus({
          has_key: res.data.has_key,
          provider: res.data.provider,
          base_url: isEndpointProvider ? baseUrl.trim() : null,
          model: model.trim(),
        });
        setApiKeyInput("");
        if (MODEL_PICKER_PROVIDERS.includes(provider)) {
          rememberConfig({ provider, baseUrl: isEndpointProvider ? baseUrl.trim() : "", model: model.trim(), freeOnly });
        }
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to connect AI provider.");
      })
      .finally(() => setLoading(false));
  };

  const disconnect = () => {
    setLoading(true);
    axios
      .delete("/api/ai/key")
      .then((res) => {
        reportStatus(res.data);
        // Ready the form to reconnect the same provider with its saved key.
        if (res.data.saved_provider) {
          setProvider(res.data.saved_provider);
          setBaseUrl(res.data.saved_base_url || "");
          setModel(res.data.saved_model || "");
        }
      })
      .catch(() => setError("Failed to disconnect."))
      .finally(() => setLoading(false));
  };

  const testConnection = () => {
    setTesting(true);
    setTestResult(null);
    axios
      .post("/api/ai/key/test")
      .then((res) => setTestResult({ ok: true, text: `Connection works (${res.data.latency_ms} ms).` }))
      .catch((err) => setTestResult({ ok: false, text: err.response?.data?.error || "Connection test failed." }))
      .finally(() => setTesting(false));
  };

  const connectDisabled =
    (!apiKeyInput.trim() && !isEndpointProvider && !hasSavedKey) ||
    (isEndpointProvider && !baseUrl.trim());

  return (
    <div className="settings-card">
      <h3 className="settings-card-title">AI Provider</h3>
      <Message
        severity="info"
        style={{ marginBottom: "0.75rem", width: "100%", justifyContent: "flex-start" }}
        content={
          <span>
            Optional. Used only for AI-generated text: the {ASSISTANT_NAME} chat and the DOE Analyzer's
            narrative and follow-up chat. Never for the charts or statistics.
          </span>
        }
      />
      {status.hasKey ? (
        <div className="settings-row">
          <Tag severity="success" value={`Connected (${describeProviderStatus(status)})`} />
          <Button label="Test connection" outlined size="small" onClick={testConnection} loading={testing} />
          <Button label="Disconnect" outlined size="small" onClick={disconnect} loading={loading} />
          {testResult && <Message severity={testResult.ok ? "success" : "error"} text={testResult.text} />}
        </div>
      ) : (
        <div className="settings-column">
          <div className="settings-row">
            <Dropdown
              value={provider}
              options={visibleProviders}
              onChange={(e) => {
                setProvider(e.value);
                // Don't carry another provider's model or endpoint over.
                setModel(e.value === "openrouter" ? OPENROUTER_FREE_ROUTER : "");
                setModelOptions([]);
                if (!ENDPOINT_PROVIDERS.includes(e.value)) setBaseUrl("");
              }}
              style={{ minWidth: "220px" }}
            />
            <Password
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder={hasSavedKey ? "Saved key, leave blank to reuse" : isEndpointProvider ? "API key (optional)" : "API key"}
              feedback={false}
              toggleMask
            />
          </div>
          {isEndpointProvider && (
            <>
              {provider === "local" && (
                <div className="settings-row">
                  <Dropdown
                    options={LOCAL_PRESETS}
                    optionLabel="label"
                    placeholder="Quick-fill a local engine..."
                    onChange={(e) => {
                      setBaseUrl(e.value.baseUrl);
                      setModel("");
                      setModelOptions([]);
                    }}
                    style={{ minWidth: "320px" }}
                  />
                </div>
              )}
              <div className="settings-row">
                <InputText
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://your-openwebui-host/api/chat/completions"
                  style={{ minWidth: "320px" }}
                />
                <Button
                  label="Fetch models"
                  outlined
                  size="small"
                  onClick={() => fetchModels()}
                  loading={fetchingModels}
                  disabled={!baseUrl.trim()}
                />
              </div>
            </>
          )}
          {isOpenRouter && (
            <div className="settings-row">
              <Checkbox inputId="ai-free-only" checked={freeOnly} onChange={(e) => setFreeOnly(e.checked)} />
              <label htmlFor="ai-free-only">
                Free models only{modelOptions.length > 0 ? ` (${freeCount} of ${modelOptions.length})` : ""}
              </label>
              <Button
                label="Refresh models"
                icon="pi pi-refresh"
                text
                size="small"
                onClick={() => fetchModels()}
                loading={fetchingModels}
              />
            </div>
          )}
          {MODEL_PICKER_PROVIDERS.includes(provider) && (
            <div className="settings-row">
              <Dropdown
                value={model}
                options={shownModelOptions}
                onChange={(e) => setModel(e.value || "")}
                editable
                filter={isOpenRouter}
                placeholder={
                  isOpenRouter ? "Free Models Router (or pick / type a model id)" : "model name (fetch above, or type one)"
                }
                itemTemplate={(option) => (
                  <span>
                    {option.label}
                    {isOpenRouter && option.free && !freeOnly && (
                      <Tag value="free" severity="success" style={{ marginLeft: "0.5rem", fontSize: "0.7rem" }} />
                    )}
                  </span>
                )}
                style={{ width: "min(480px, 100%)" }}
              />
            </div>
          )}
          <div className="settings-row">
            <Button label="Connect" onClick={connect} loading={loading} disabled={connectDisabled} />
          </div>
        </div>
      )}
      {error && <Message severity="error" text={error} />}
      <small className="settings-muted">
        {allows("anthropic") && (
          <>
            Anthropic keys: <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">console.anthropic.com</a>.{" "}
          </>
        )}
        OpenAI keys: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com</a>.
        {allows("openrouter") && (
          <>
            {" "}OpenRouter keys: <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">openrouter.ai/keys</a> -- free models
            still need a key, and have tighter rate limits.
          </>
        )}
        {allows("local") && (
          <>
            {" "}Local: pick a preset for Ollama or Docker Model Runner, or point at your Open WebUI host's
            chat-completions endpoint -- no key needed unless that host requires one.
          </>
        )}
        {" "}The chosen model and endpoint are remembered in this browser. Keys are stored encrypted for this session
        only -- you'll reconnect after ~5 hours or on a new device. Disconnecting keeps the key so you can reconnect
        the same provider without retyping it; connecting a different provider replaces it.
      </small>
    </div>
  );
}

export default AIProviderPanel;
